import "./ClientBankSetupStyle.css";

import * as yup from "yup";

import {
	ACCOUNTANT_SETUP_SCREEN_SLUG,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../../common/constants";
import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedDialog,
	EnhancedSnackbar,
	SingleColumnLayout,
	StandardPanel,
} from "@websential/cosmic-v20";
import { Box, Button, Card, Grid, Typography, useTheme } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import {
	IGetListRequest,
	IResponse,
	IServerError,
} from "../../../models/common";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	formatDate,
	sortData,
	sortDDLByLabel,
	useNavigateToLogout,
} from "../../../common/Common";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Avatar } from "primereact/avatar";
import { BankService } from "../../../services/BankService";
import { ClientBankInfoSetupFieldsPanel } from "./Panels/ClientBankInfoSetupFieldsPanel";
import { ClientBankSetupToolbar } from "./ClientBankSetupToolbar/ClientBankSetupToolbar";
import { ClientBankSetupValidationSchema } from "./validator";
import { ClientService } from "../../../services";
import { CountryService } from "../../../services/common/CountryService";
import { DDLDataTable } from "../../DDLDataTable";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { EnhancedButtonPrimeReact } from "../../EnhancedButtonPrimeReact";
import { IAppState } from "../../../store/reducers";
import { IClient } from "../../../models/client";
import { InputText } from "primereact/inputtext";
import { VerifyBankDialogue } from "../../VerifyBankDialogue";
import { setSelectedActivityLogActions } from "../../../store/actions/activityLogActions";
import { setSelectedDrawerOptionsMenu } from "../../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../../store/actions/stickyNavBarActions";
import { yupResolver } from "@hookform/resolvers/yup";

export interface IClientBankSetup { }
const SINGLE_RECORD_DATA_QUERY_KEY = "client-single-data-record";
const CLIENT_BANKS_DATA_QUERY_KEY = "client-bank-list";
const CLIENT_BANKS_DDL_DATA_QUERY_KEY = "client-bank-ddl";

const useGetData = (
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, id],
		queryFn: () => {
			return ClientService.getClientBankDataById(id, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});
const useGetClientDDL = (accountantId: string) =>
	useQuery({
		queryKey: ["Client-ddl"],
		queryFn: () => {
			return ClientService.getGetClientDDL(accountantId);
		},
		refetchOnWindowFocus: false,
	});

const useGetClientBankList = (
	clientId: string,
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [CLIENT_BANKS_DATA_QUERY_KEY, clientId],
		queryFn: (data) => {

			return ClientService.getGetClientBankList(
				clientId,
				params,
				navigateToLogout
			);
		},
		refetchOnWindowFocus: false,
	});
const useGetClientBankDDL = (clientId: string | undefined) =>
	useQuery({
		queryKey: [CLIENT_BANKS_DDL_DATA_QUERY_KEY],
		queryFn: () => {
			return !clientId ? null : ClientService.getGetClientBankDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});
const useGetCountryDDL = () =>
	useQuery({
		queryKey: ["country-ddl"],
		queryFn: (data) => {
			return CountryService.getCountryDDL();
		},
		refetchOnWindowFocus: false,
	});
export const ClientBankSetup: React.FC<IClientBankSetup> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const theme = useTheme();
	const subMenuVisibility = useSelector(
		(state: IAppState) => state.drawerAction.subMenuVisibility
	);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const [clientIdState, setClientId] = useState(clientId);
	const [openDialog, setDialogOpen] = useState(false);
	const [isPendingData, setIsPendingData] = useState(false);
	const accountantId = useSelector((state: IAppState) => state.accountant.id);
	const [openArchiveDialog, setArchiveDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [singleArchive, setSingleArchive] = useState<boolean>(false);
	const [clientBanksId, setClientBanksId] = useState<number | string>(0);
	const [itemSearch, setItemSearch] = useState("");
	const [sidePanelsVisibility, setSidePanelsVisibility] = useState(true);
	const [selectedCard, setSelectedCard] = useState<any>(null);
	// const [clientBanksList, setClientBanksList] =
	// 	useState<any>(undefined);
	const [clientBankId, setClientBankId] = useState<number>(0);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const queryClient = useQueryClient();
	const isAdminUser = useSelector(
		(state: IAppState) => state.user.profile.isSuperStatus
	);
	const navigateToLogout = useNavigateToLogout();
	const { id } = useParams();

	const [editRecordId, setEditRecordId] = useState(id);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "name",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	// [Verify]
	const [openVerifyBankDialogue, setOpenverifyBankDialogue] = useState(false)

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null ? requestParams.sortField : "name",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	const { isPending, data } = useGetClientBankList(
		clientIdState,
		queryParams,
		navigateToLogout
	);

	const clientBanksList: any | null = data;

	const { isPending: isSubmitting, mutate: submitClientBankFormFields } =
		useMutation({
			mutationFn: ClientService.createClientBank,
			onSuccess: (res: IClient | IServerError | IResponse | any) => {

				if (res.status === 200) {
					// if (instanceOfIClientResponse(res)) {
					setEditRecordId(res.data.id);
					queryClient.invalidateQueries({
						queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
					});
					queryClient.invalidateQueries({
						queryKey: [CLIENT_BANKS_DATA_QUERY_KEY],
					});
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					let path = `/clients/bank-setup/${res.data.id}`;
					navigate(path);
					// (async () => {
					// 	try {
					// 		setIsPendingData(true);
					// 		const clientBanksDDL =
					// 			await ClientService.getGetClientBankDDL(
					// 				res.data.clientId
					// 			);
					// 		if (Array.isArray(clientBanksDDL)) {
					// 			const sortedList = sortData(clientBanksDDL);
					// 			const filteredList =
					// 				sortedList &&
					// 				sortedList.filter((item: any) =>
					// 					item.label.toLowerCase().includes(itemSearch.toLowerCase())
					// 				);
					// 			setClientBanksList(filteredList);
					// 		}
					// 		let path = `/clients/bank-setup/${res.data.id}`;
					// 		navigate(path);
					// 	} catch {

					// 	} finally {
					// 		setIsPendingData(false);
					// 	}
					// })();
				} else if (res.status === 201) {
					// if (instanceOfIClientResponse(res)) {
					setEditRecordId(res.data.id);
					queryClient.invalidateQueries({
						queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
					});
					queryClient.invalidateQueries({
						queryKey: [CLIENT_BANKS_DATA_QUERY_KEY],
					});
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					let path = `/clients/bank-setup/${res.data.id}`;
					navigate(path);
					// (async () => {
					// 	try {
					// 		setIsPendingData(true);
					// 		const clientBanksDDL =
					// 			await ClientService.getGetClientBankDDL(
					// 				res.data.clientId
					// 			);
					// 		if (Array.isArray(clientBanksDDL)) {
					// 			const sortedList = sortData(clientBanksDDL);
					// 			const filteredList =
					// 				sortedList &&
					// 				sortedList.filter((item: any) =>
					// 					item.label.toLowerCase().includes(itemSearch.toLowerCase())
					// 				);
					// 			setClientBanksList(filteredList);
					// 		}
					// 		let path = `/clients/bank-setup/${res.data.id}`;
					// 		navigate(path);
					// 	} catch {

					// 	} finally {
					// 		setIsPendingData(false);
					// 	}
					// })();
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const ExtendedClientBankSetupValidationSchema = ClientBankSetupValidationSchema.shape({
		clientId: clientId
			? yup.string().notRequired().nullable()
			: yup.string().required("Client is required"),
	});

	const DEFAULT_VALUES = {
		name: undefined,
		accountNo: undefined,
		branchTransitNumber: undefined,
		bankFinancialNo: undefined,
		primaryAccount: false,
		status: 200,
	};

	const formMethods = useForm<any>({
		resolver: yupResolver(ExtendedClientBankSetupValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES,
	});

	const { data: selectedDataRecord } = useGetData(
		editRecordId,
		navigateToLogout
	);

	const { data: clientDDL } = useGetClientDDL(accountantId);

	const upadtedClientBanksListItems = useMemo(() => {
		return (
			clientBanksList &&
			clientBanksList.items &&
			clientBanksList?.items.map((item: any) => ({
				...item,
				label: (
					<Link
						to={`/clients/bank-setup/${item.id}`}
						className="no-underline"
						style={{ color: theme.palette.primary.light }}
						onClick={() => setEditRecordId(item.id)}
					>
						{item.name}
					</Link>
				),
			}))
		);
	}, [clientBanksList]);

	const onClientBankSelectionChangeHandler = async (params: any) => {
		let path = `/clients/bank-setup`;
		navigate(path);
		formMethods.setValue('name', null)
		formMethods.setValue('accountNo', null)
		formMethods.setValue('branchTransitNumber', null)
		formMethods.setValue('bankFinancialNo', null)
		formMethods.setValue('primaryAccount', false)
		formMethods.setValue('status', 200)
		setClientId(params.value);
		// try {
		// 	setIsPendingData(true);
		// 	const clientBanksDDL =
		// 		await ClientService.getGetClientBankDDL(params.value);
		// 	if (Array.isArray(clientBanksDDL)) {
		// 		const sortedList = sortData(clientBanksDDL);
		// 		const filteredList =
		// 			sortedList &&
		// 			sortedList.filter((item: any) =>
		// 				item.label.toLowerCase().includes(itemSearch.toLowerCase())
		// 			);
		// 		setClientBanksList(filteredList);
		// 	}
		// } catch {

		// } finally {
		// 	let path = `/clients/bank-setup`;
		// 	navigate(path);
		// 	formMethods.setValue('name',"")
		// 	formMethods.setValue('accountNo',"")
		// 	formMethods.setValue('branchTransitNumber',"")
		// 	formMethods.setValue('bankFinancialNo',"")
		// 	formMethods.setValue('primaryAccount',false)
		// 	formMethods.setValue('status',200)
		// 	setSelectedCard(null);
		// 	setIsPendingData(false);
		// }
	};

	// const { data: clientBankLabelsList } = useGetClientBankDDL(clientId);

	// useEffect(() => {

	// 	if (Array.isArray(clientBankLabelsList)) {
	// 		setClientBanksList(sortData(clientBankLabelsList));
	// 	} else {
	// 		setClientBanksList(undefined)
	// 	}
	// }, [clientBankLabelsList]);

	// const filteredList =
	// 	clientBanksList &&
	// 	clientBanksList.filter((item: any) =>
	// 		item.label.toLowerCase().includes(itemSearch.toLowerCase())
	// 	);

	// const { isPending: isPendingCountryDDL, data: countryList } =
	// 	useGetCountryDDL();
	// let countryList = [
	// 	{
	// 		shortCode: "CAN",
	// 		value: "6718c0e02bd6872c87d35d0f",
	// 		label: "Canada",
	// 		countryType: "Province",
	// 	},
	// 	{
	// 		logo: "",
	// 		shortCode: "USA",
	// 		value: "67496d974b9fb9f2ce4213b9",
	// 		label: "United States of America",
	// 		countryType: "state",
	// 	},
	// ];

	const [openBranchAddedDialog, setOpenBranchAddedDialog] = useState(false);
	const [pendingValues, setPendingValues] = useState<any>(null);
	const onCloseBranchAddedHandler = () => {
		setOpenBranchAddedDialog(false)
	}
	const onConfirmBranchSubmit = () => {
		if (pendingValues) {
			setOpenBranchAddedDialog(false);
			setPendingValues(null);
			let payload = {
				navigateToLogout: navigateToLogout,
				id: id && id,
				clientId: clientId ? clientId : pendingValues.clientId,
				name: pendingValues.name,
				accountNo: pendingValues.accountNo,
				branchTransitNumber: pendingValues.branchTransitNumber,
				bankFinancialNo: pendingValues.bankFinancialNo,
				primaryAccount: pendingValues.primaryAccount,
				status: pendingValues.status,
			};
			// alert(JSON.stringify(payload, null, 2));
			submitClientBankFormFields(payload);
		}
	};

	const onSubmit = async (values: any) => {
		try {
			setIsPendingData(true)
			const verifyBank = await BankService.bankOrBranchExists(values.bankFinancialNo, values.branchTransitNumber);
			console.log("verifyBank", verifyBank)
			if (verifyBank && verifyBank.bankExist && verifyBank.branchExist) {
				let payload = {
					navigateToLogout: navigateToLogout,
					id: id && id,
					clientId: clientId ? clientId : values.clientId,
					name: values.name,
					accountNo: values.accountNo,
					branchTransitNumber: values.branchTransitNumber,
					bankFinancialNo: values.bankFinancialNo,
					primaryAccount: values.primaryAccount,
					status: values.status,
				};
				// alert(JSON.stringify(payload, null, 2));
				submitClientBankFormFields(payload);
			}
			if (verifyBank && !verifyBank.bankExist) {
				setSnackbarData({
					open: true,
					message: "Bank does not exist",
					severity: "error"
				})
			}
			if (verifyBank && verifyBank.bankExist && !verifyBank.branchExist) {
				setPendingValues(values);
				setOpenBranchAddedDialog(true);
			}

			setIsPendingData(false)
		}
		catch { setIsPendingData(false) }

	};

	useEffect(() => {
		if (id && selectedDataRecord) {

			setIsPendingData(true);
			Object.keys(selectedDataRecord).forEach((fieldName) => {
				const fieldValue = selectedDataRecord[fieldName];
				formMethods.setValue(fieldName, String(fieldValue) ?? undefined);
				if (fieldName === "primaryAccount") {
					formMethods.setValue(fieldName, fieldValue ?? undefined);
				}
				if (fieldName === "status") {
					formMethods.setValue(fieldName, fieldValue ?? undefined);
				}
				// if (fieldName === "clientId" && fieldValue && fieldValue !== "") {
				// 	(async () => {
				// 		try {
				// 			const clientBanksDDL =
				// 				await ClientService.getGetClientBankDDL(fieldValue);
				// 			if (Array.isArray(clientBanksDDL)) {
				// 				const sortedList = sortData(clientBanksDDL);
				// 				const filteredList =
				// 					sortedList &&
				// 					sortedList.filter((item: any) =>
				// 						item.label.toLowerCase().includes(itemSearch.toLowerCase())
				// 					);
				// 				setClientBanksList(filteredList);
				// 			}
				// 		} catch {
				// 	
				// 		}
				// 	})();
				// }
				// if (fieldName === "id") {
				// 	setSelectedCard(String(fieldValue));
				// }
			});
			setIsPendingData(false);
		}
	}, [id, selectedDataRecord]);

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteClientById } = useMutation({
		mutationFn: ClientService.deleteClientBankById,

		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {
				// remove the cache of the Client fields since we just added a new field.
				queryClient.invalidateQueries({
					queryKey: [CLIENT_BANKS_DATA_QUERY_KEY],
				});
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
			formMethods.reset();
			setEditRecordId(undefined)
			let path = `/clients/bank-setup`;
			navigate(path);
		},
		onError: () => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const deleteClientField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let payload = {
				clientBankId: clientBankId
			}
			deleteClientById(payload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord) {
			setClientBankId(selectedDataRecord.id);
		}
	};

	const onRowDeleteClicked = useCallback((params: any) => {

		setDialogOpen(true);
		setSingleDelete(true);
		setClientBankId(params.id);
	}, []);

	const { isPending: isArchive, mutate: archiveClientBanksById } = useMutation({
		mutationFn: ClientService.archiveClientBanks,

		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {
				// remove the cache of the ClientBanks fields since we just added a new field.
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
			formMethods.reset();
			let path = `/clients/bank-setup`;
			navigate(path);
			return queryClient.invalidateQueries({
				queryKey: [CLIENT_BANKS_DATA_QUERY_KEY],
			});
		},
		onError: () => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const archiveClientBanksField = () => {
		if (singleArchive) {
			setSingleArchive(false);
			let ArchivePayload = {
				clientBanksId: clientBanksId,
				navigateToLogout: navigateToLogout,
			};
			archiveClientBanksById(ArchivePayload);
			setArchiveDialogOpen(false);
		}
	};

	const onArchiveClicked = () => {
		setArchiveDialogOpen(true);
		setSingleArchive(true);
		if (selectedDataRecord) {
			setClientBanksId(selectedDataRecord.id);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};
	const onCloseArchiveHandler = () => {
		setArchiveDialogOpen(false);
	};

	const onRowEditClicked = useCallback((params: any) => {
		// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
		let path = `/clients/bank-setup/${params.value}`;
		navigate(path);
	}, []);

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
		let path = `/clients/bank-setup`;
		navigate(path);
	};

	const expandedLabels = ["Home"];
	const expandedLabelsInsideCompany = ["Company"];
	const expandedHomeBankSetupOrSetupItem = "Client Bank Setup"; // Special case for "Setup" under "City Tax"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			if (clientId && clientId !== "") {
				let isExpanded = expandedLabelsInsideCompany.includes(item.label);
				if (parentLabel === "Company" && item.label === "Bank") {
					isExpanded = true;
				}
				const enhancedItem = {
					...item,
					expanded: item.items ? isExpanded : false,
					items: item.items ? enhanceData(item.items, item.label) : undefined,
					path: item.label === "Bank" && `/clients/bank-setup`,
				};
				return enhancedItem;
			} else {
				let isExpanded = expandedLabels.includes(item.label);
				if (
					parentLabel === "Home" &&
					item.label === expandedHomeBankSetupOrSetupItem
				) {
					isExpanded = true;
				}
				const enhancedItem = {
					...item,
					expanded: item.items ? isExpanded : false,
					items: item.items ? enhanceData(item.items, item.label) : undefined,
					path: item.label === expandedHomeBankSetupOrSetupItem && "/clients/bank-setup",
				};
				return enhancedItem;
			}
		});
	};

	// const enhanceData = (data: any[]): any[] => {
	// 	return data.map((item) => {
	// 		const enhancedItem = {
	// 			...item,
	// 			expanded: item.label === "Setup" ? true : false,
	// 		};
	// 		return enhancedItem;
	// 	});
	// };

	// const onBankSelectionChangeHandler = async (
	// 	selectedItem: any,
	// 	index?: any
	// ): Promise<void> => {


	// 	//this is for card selection
	// 	setSelectedCard(index);
	// 	setEditRecordId(selectedItem.value);
	// 	queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
	// 	let path = `/clients/bank-setup/${selectedItem.value}`;
	// 	navigate(path);
	// 	setIsPendingData(true)
	// 	setTimeout(() => {
	// 		setIsPendingData(false)
	// 	}, 1000);

	// 	if (!index) {
	// 		//this is for card selection
	// 		setSelectedCard(selectedItem.value);
	// 	}
	// };

	// const getCardStyles = (index: any) => {

	// 	return {
	// 		borderLeft:
	// 			selectedCard === index ? "6px solid #0091b3" : "2px solid transparent",
	// 		cursor: "pointer",
	// 	};
	// };

	// const onCreateNewButtonClicked = () => {
	// 	setSnackbarData({
	// 		open: true,
	// 		message: "Create New Bank",
	// 		severity: "warning",
	// 	});
	// 	formMethods.reset();
	// 	let path = `/clients/bank-setup`;
	// 	navigate(path);
	// 	// setIsPendingAdjustedData(true);
	// 	// setTimeout(() => {
	// 	//   setIsPendingAdjustedData(false);
	// 	// }, 1000);
	// 	setSelectedCard(null);
	// 	setClientBanksList(undefined);
	// };
	// const onDividerButtonClickHandler = () => {
	// 	setSidePanelsVisibility((prevButtonState: any) => !prevButtonState);
	// };

	useEffect(() => {
		// dispatch(
		// 	setSelectedActivityLogActions({
		// 		entityId1: "",
		// 		entityId2: "",
		// 		entityId3: "",
		// 		screenName: ACCOUNTANT_SETUP_SCREEN_SLUG,
		// 		clientName: "",
		// 		activityType:
		// 			"activity_type=create&activity_type=updated&activity_type=delete",
		// 		activityLogVisibility: true,
		// 	})
		// );
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	return (
		<Grid
		// sx={{
		// 	pb: "5rem",
		// 	"& .MuiAppBar-root": {
		// 		zIndex: 2,
		// 		top: subMenuVisibility ? "154px" : "112px",
		// 	},
		// }}
		>
			<BackdropCircularProgress
				open={isSubmitting || isPendingData || isDeleting || isArchive}
				testId="comapny Setup"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteClientField}
				cancelActionHandler={onCloseHandler}
				testId="client-confirmation-dialog"
			/>
			{/* {sidePanelsVisibility && (
					<Grid item xs={2} sm={2} md={2} lg={2} xl={2}>
						<Box sx={{ margin: "0" }}>
							<InputText
								value={itemSearch}
								onChange={(e) => setItemSearch(e.target.value)}
								placeholder={"Search by name..."}
								className="w-full search-field"
							/>
							<Box sx={{ display: "flex", justifyContent: "end" }}>
								<Button
									variant="contained"
									sx={{ right: 0, margin: "10px 0px", fontSize: "12px" }}
									onClick={onCreateNewButtonClicked}
									disabled={!clientBanksList ? true : false}
								>
									+ Create New
								</Button>
							</Box>
						</Box>
						<Box
							sx={{
								// display: "flex",
								height: "100%",
								// flexDirection: "column",
								overflow: "auto",
								"&::-webkit-scrollbar": {
									width: "0.4em",
								},
								"&::-webkit-scrollbar-thumb": {
									background: "#ccc",
									borderRadius: "3px",
								},
								"&::-webkit-scrollbar-thumb:hover": {
									backgroundColor: "#aaa",
								},
							}}
						>
							{clientBanksList &&
								clientBanksList.map((item: any, index: any) => {
									return (
										<Card
											sx={{
												padding: 0,
												borderRadius: "8px",
												marginBottom: "1rem",
											}}
											key={index}
											onClick={() =>
												onBankSelectionChangeHandler(item, item.value)
											}
											style={getCardStyles(item.value)}
										>
											<Box
												sx={{
													display: "flex",
													justifyContent: "space-between",
													padding: "0.8rem",
													alignItems: "center",
												}}
											>
												<Typography
													sx={{ fontWeight: "bold", fontSize: ".875rem" }}
												>
													{item.label}
												</Typography>
												<Avatar
													shape="circle"
													style={{
														background: "#0091b3",
														color: "#fff",
														borderRadius: "50%",
														fontWeight: "bold",
														fontSize: ".775rem",
														padding: "2px",
													}}
												>
													{item.label
														.replace(/\s+/g, " ")
														.split(" ")
														.filter((word: string) => word.trim() !== "")
														.map((word: string) => word[0].toUpperCase())
														.slice(0, 2)
														.join("")}
												</Avatar>
											</Box>
										</Card>
									);
								})}
						</Box>
					</Grid>
				)} */}
			{/* <Divider
					layout="vertical"
					align="center"
					className="verticle-divider"
					style={{ zIndex: 0 }}
				>
					<Button
						variant="outlined"
						sx={{
							minWidth: "fit-content",
							padding: "20px 0",
							borderRadius: "7px",
						}}
						onClick={onDividerButtonClickHandler}
					>
						{sidePanelsVisibility ? (
							<i
								className="pi pi-angle-double-left"
								style={{ color: "#0091b3" }}
							></i>
						) : (
							<i
								className="pi pi-angle-double-right"
								style={{ color: "#0091b3" }}
							></i>
						)}
					</Button>
				</Divider> */}
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 5, lg: 4.5, xl: 3.5 }}>
							<SingleColumnLayout
								children={
									<StandardPanel
										title="Client Bank Setup"
										testId={`client-bank-setup`}
										showTitle={false}
										variant="outlined"
										topPadding={2}
									>
										<ClientBankInfoSetupFieldsPanel
											isAdminUser={isAdminUser}
											clientDDL={clientDDL && sortDDLByLabel(clientDDL)}
											onClientBankSelectionChangeHandler={
												onClientBankSelectionChangeHandler
											}
											clientId={clientId}
											onVerifyBankClickedHandler={() => setOpenverifyBankDialogue(true)}
											selectedDataRecord={selectedDataRecord}
										/>
									</StandardPanel>
								}
								toolbar={
									<ClientBankSetupToolbar
										onResetClicked={onResetClicked}
										onDeleteClicked={onDeleteClicked}
										isSubmitting={false}
										id={id}
										selectedDataRecord={selectedDataRecord}
									/>
								}
								toolbarPosition="bottom"
								showFullDiaplay={true}
								testId="form-layout"
							/>
						</Grid>
						<Grid size={{ xs: 12, sm: 12, md: 2, lg: 3.4, xl: 5.4 }}></Grid>
						<Grid
							size={{ xs: 12, sm: 12, md: 5, lg: 4.1, xl: 3.1 }}
							sx={{ marginTop: "16px", display: { xs: "none", sm: "none", md: "block" } }}
						>
							<DDLDataTable
								rows={
									clientBanksList && upadtedClientBanksListItems
										? upadtedClientBanksListItems
										: []
								}
								onRowEditClicked={onRowEditClicked}
								onRowDeleteClicked={onRowDeleteClicked}
								disabledDeleteIcon={selectedDataRecord && selectedDataRecord.status !== 100 && isAdminUser !== 400 ? false : true}
								onRowArchiveClicked={onArchiveClicked}
								isLoading={isPending}
								totalRecords={
									clientBanksList && clientBanksList.total
										? clientBanksList.total
										: 0
								}
								initialPageSize={INITIAL_PAGE_SIZE}
								rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
								onRequestParamsChange={onRequestParamsChange}
								tableScrollHeight={"60vh"}
								selectedId={selectedDataRecord && id && selectedDataRecord.id}
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid>
					</Grid>
				</form>
			</FormProvider>

			{/* Archive Dialogue */}
			<EnhancedDialog
				dialogTitle="Please Confirm"
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onCloseArchiveHandler}
				open={openArchiveDialog}
				dialogContent={null}
				showdividers={false}
				dialogAction={
					<Grid>
						<EnhancedButtonPrimeReact
							label="Cancel"
							severity="secondary"
							text
							raised
							onClickHandler={onCloseArchiveHandler}
							style={{ fontSize: "small" }}
						/>
						<EnhancedButtonPrimeReact
							label="Archive"
							severity="secondary"
							raised
							onClickHandler={() => archiveClientBanksField}
							style={{ marginLeft: "16px", fontSize: "small" }}
						/>
					</Grid>
				}
			/>
			<VerifyBankDialogue openVerifyBankDialogue={openVerifyBankDialogue} setOpenVerifyBankDialogue={(value: any) => setOpenverifyBankDialogue(value)} parentFormMethods={formMethods} />
			<EnhancedDialog
				dialogTitle="Please Confirm"
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onCloseBranchAddedHandler}
				open={openBranchAddedDialog}
				dialogContent={
					<Box>
						<Typography variant="body2" sx={{ mb: 2 }}>
							Account 1 Branch / Transit No. is not a valid code as listed in our current banking records. If you believe this is correct, please mail us.
						</Typography>
						<Typography variant="body2">
							Note: Penalty amount will be deducted for providing incorrect bank details.
						</Typography>
					</Box>
				}
				showdividers={false}
				dialogAction={
					<Grid>
						<EnhancedButtonPrimeReact
							label="Cancel"
							severity="secondary"
							text
							raised
							onClickHandler={onCloseBranchAddedHandler}
							style={{ fontSize: "small" }}
						/>
						<EnhancedButtonPrimeReact
							label="Ok"
							severity="success"
							raised
							onClickHandler={onConfirmBranchSubmit}
							style={{ marginLeft: "16px", fontSize: "small" }}
						/>
					</Grid>
				}
			/>
			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"client-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
